import {
  cpscRecordsToRecallItems,
  fdaRecordsToRecallItems,
  sortRecallItemsByDate,
  usdaRecordsToRecallItems,
  vehicleRecallsToRecallItems,
} from './useRecallsApi';

import type {
  RecallsFieldConfig,
  RecallsPillConfig,
  RecallsSortValue,
} from './constants';
import type {
  CpscRecallRecord,
  FdaEnforcementRecord,
  HookData,
  NormalizedRecallCard,
  RecallCardMetricTileData,
  RecallItem,
  RecallType,
  RecallsSearchParams,
  ResultsPayload,
  UsdaRecallRecord,
  VehicleRecallRecord,
  VehicleRecallsParams,
} from './interfaces';
import type { VehicleFormData } from '../../../SearchHub/Vehicle/utils';

export const defaultFieldConfigs: RecallsFieldConfig[] = [];

export function buildValuesForPill(
  searchTypeOptions: RecallsPillConfig['searchTypeOptions'],
  fieldConfigs: RecallsFieldConfig[],
  savedDropdown:
    | { searchType: string; initialValues?: Partial<VehicleFormData> }
    | undefined,
  sharedSearchValue: string
): VehicleFormData {
  const firstSearchType = searchTypeOptions[0]?.value ?? '';
  const validSearchTypes = new Set(searchTypeOptions.map((o) => o.value));
  const searchType =
    savedDropdown?.searchType && validSearchTypes.has(savedDropdown.searchType)
      ? savedDropdown.searchType
      : firstSearchType;

  const base: VehicleFormData = {
    searchType: searchType as VehicleFormData['searchType'],
    query: sharedSearchValue,
    vin: '',
    year: '',
    make: '',
    model: '',
    trim: '',
    licensePlate: '',
    state: 'All',
    ...Object.fromEntries(
      fieldConfigs.map((f) => [f.name, f.type === 'select' ? '' : ''])
    ),
    ...(savedDropdown?.initialValues ?? {}),
  };

  return base;
}

/**
 * Normalize recall date to YYYY-MM-DD for display.
 * Handles: "20230118" → "2023-01-18"; "2026-02-27T00:00:00" → "2026-02-27".
 */
export function formatRecallDate(dateStr: string): string {
  const s = dateStr?.trim() ?? '';
  if (!s) {
    return '';
  }
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  return s.slice(0, 10);
}

/** Coerce unknown to trimmed string; null/undefined → "". */
export function str(value: unknown): string {
  return String(value ?? '').trim();
}

/** Coerce to trimmed string; null/undefined/empty → undefined. */
export function strOrUndefined(value: unknown): string | undefined {
  const s = str(value);
  return s === '' ? undefined : s;
}

function convertRecallClassSeverity(label: string): string {
  const s = label.toLowerCase();
  if (s.includes('class iii')) {
    return 'Low';
  }
  if (s.includes('class ii')) {
    return 'Moderate';
  }
  if (s.includes('class i')) {
    return 'High';
  }
  return label;
}

export function recallPillsForUsdaCard(raw: unknown): string[] | undefined {
  const v = str(raw);
  if (!v) {
    return undefined;
  }
  const pills = v
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return pills.length > 0 ? pills : undefined;
}

export function recallSeverityForCard(raw: unknown): string | undefined {
  const v = str(raw);
  return v ? convertRecallClassSeverity(v) : undefined;
}

export function isHighOrCriticalRecallSeverity(
  severity: string | undefined
): boolean {
  const s = severity?.trim().toLowerCase();
  return s === 'high' || s === 'critical';
}

function formatRecallTypeTileLabel(recallType: RecallType): string {
  return recallType
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function buildAgencyRecallMetricTiles(
  card: Pick<NormalizedRecallCard, 'severity' | 'recallDate'>
): RecallCardMetricTileData[] | undefined {
  const tiles: RecallCardMetricTileData[] = [];

  const severity = str(card.severity);
  if (severity) {
    tiles.push({
      label: 'Risk level',
      value: severity,
      valueEmphasis: isHighOrCriticalRecallSeverity(severity),
    });
  }

  const recallDate = str(card.recallDate);
  if (recallDate) {
    tiles.push({ label: 'Recall date', value: recallDate });
  }

  return tiles.length > 0 ? tiles : undefined;
}

function buildConsumerProductRecallMetricTiles(
  card: Pick<NormalizedRecallCard, 'recallDate' | 'options'>
): RecallCardMetricTileData[] | undefined {
  const tiles: RecallCardMetricTileData[] = [];

  const options = str(card.options);
  if (options) {
    tiles.push({ label: 'Options', value: options });
  }

  const recallDate = str(card.recallDate);
  if (recallDate) {
    tiles.push({ label: 'Recall date', value: recallDate });
  }

  return tiles.length > 0 ? tiles : undefined;
}

function buildVehicleRecallMetricTiles(
  card: Pick<
    NormalizedRecallCard,
    'recallDate' | 'recallSubType' | 'recallType' | 'severity'
  >
): RecallCardMetricTileData[] | undefined {
  const tiles: RecallCardMetricTileData[] = [];

  const severity = str(card.severity);
  if (severity) {
    tiles.push({
      label: 'Risk level',
      value: severity,
      valueEmphasis: isHighOrCriticalRecallSeverity(severity),
    });
  }
  const typeValue =
    str(card.recallSubType) || formatRecallTypeTileLabel(card.recallType);
  if (typeValue) {
    tiles.push({ label: 'Type', value: typeValue });
  }

  const recallDate = str(card.recallDate);
  if (recallDate) {
    tiles.push({ label: 'Recall date', value: recallDate });
  }

  return tiles.length > 0 ? tiles : undefined;
}

export function normalizeRecallForCard(item: RecallItem): NormalizedRecallCard {
  const recallDate = formatRecallDate(item.date);
  const { source } = item;

  switch (source) {
    case 'fdaDrug': {
      const r = item.record as FdaEnforcementRecord;
      const severity = recallSeverityForCard(r.classification);
      return {
        recallDate,
        recallType: 'drugs' as RecallType,
        source,
        recallNumber: str(r.recall_number) || undefined,
        severity,
        company: str(r.recalling_firm),
        description: str(r.product_description),
        affectedLocations: str(r.distribution_pattern),
        metricTiles: buildAgencyRecallMetricTiles({ recallDate, severity }),
      };
    }
    case 'fdaDevice': {
      const r = item.record as FdaEnforcementRecord;
      const severity = recallSeverityForCard(r.classification);
      return {
        recallDate,
        recallType: 'medical devices' as RecallType,
        source,
        recallNumber: str(r.recall_number) || undefined,
        severity,
        company: str(r.recalling_firm),
        description: str(r.product_description),
        affectedLocations: str(r.distribution_pattern),
        metricTiles: buildAgencyRecallMetricTiles({ recallDate, severity }),
      };
    }
    case 'fdaFood': {
      const r = item.record as FdaEnforcementRecord;
      const severity = recallSeverityForCard(r.classification);
      return {
        recallDate,
        recallType: 'food' as RecallType,
        source,
        recallNumber: str(r.recall_number) || undefined,
        severity,
        company: str(r.recalling_firm),
        description: str(r.product_description),
        affectedLocations: str(r.distribution_pattern),
        metricTiles: buildAgencyRecallMetricTiles({ recallDate, severity }),
      };
    }
    case 'usda': {
      const r = item.record as UsdaRecallRecord;
      const establishment = str(r.field_establishment) || undefined;
      const fieldTitle = str(r.field_title) || undefined;
      const classification = str(r.field_recall_classification);
      const riskLevel = str(r.field_risk_level);
      const severity = recallSeverityForCard(
        classification !== ''
          ? classification
          : riskLevel !== ''
          ? riskLevel
          : undefined
      );
      return {
        recallDate,
        recallType: 'food' as RecallType,
        source,
        recallNumber: str(r.field_recall_number) || undefined,
        severity,
        pills: recallPillsForUsdaCard(r.field_recall_reason),
        company: establishment ?? fieldTitle,
        description: establishment ? fieldTitle : undefined,
        affectedLocations: str(r.field_states),
        metricTiles: buildAgencyRecallMetricTiles({ recallDate, severity }),
      };
    }
    case 'cpsc': {
      const r = item.record as CpscRecallRecord;
      const options = Array.isArray(r.RemedyOptions)
        ? r.RemedyOptions.map((x) => x?.Option)
            .filter(Boolean)
            .join('; ')
        : undefined;
      const productName =
        str(r.Title) ||
        (Array.isArray(r.Products) && r.Products[0]?.Name
          ? str(r.Products[0].Name)
          : undefined);
      const optionsValue = options || undefined;
      return {
        recallDate,
        recallType: 'consumer product' as RecallType,
        source,
        recallNumber: str(r.RecallNumber) || undefined,
        description: str(r.Description),
        options: optionsValue,
        productName: productName || undefined,
        metricTiles: buildConsumerProductRecallMetricTiles({
          recallDate,
          options: optionsValue,
        }),
      };
    }
    case 'vehicle': {
      const r = item.record as VehicleRecallRecord;
      const vehicle = str(r.oem_description) || str(r.components) || undefined;
      const campaign = str(r.campaign);
      const recallType = 'vehicle' as RecallType;
      const severity = str(r.status) || undefined;
      const recallSubType = str(r.components) || str(r.type) || undefined;
      return {
        recallDate,
        recallType,
        source,
        recallNumber: str(r.oem_recall_number) || campaign || undefined,
        severity,
        company: str(r.oem_description),
        title: campaign,
        description: str(r.summary) || str(r.consequence),
        options: str(r.remedy) || undefined,
        recallSubType,
        vehicle: vehicle || undefined,
        metricTiles: buildVehicleRecallMetricTiles({
          recallDate,
          recallType,
          severity,
          recallSubType,
        }),
      };
    }
    default: {
      return {
        recallDate,
        recallType: 'consumer product' as RecallType,
        source: 'cpsc',
        metricTiles: buildConsumerProductRecallMetricTiles({
          recallDate,
          options: undefined,
        }),
      };
    }
  }
}

export function normalizeRecallsForCards(
  items: RecallItem[]
): NormalizedRecallCard[] {
  return (Array.isArray(items) ? items : []).map(normalizeRecallForCard);
}

const SEVERITY_RANKS: Record<string, number> = {
  high: 1,
  moderate: 2,
  low: 3,
  'class i': 1,
  'class ii': 2,
  'class iii': 3,
  'public health alert': 4,
};
const UNKNOWN_SEVERITY_RANK = 999;

function severityRank(s: string): number {
  return SEVERITY_RANKS[s] ?? UNKNOWN_SEVERITY_RANK;
}

export function sortRecallsCards(
  cards: NormalizedRecallCard[],
  sortOption: RecallsSortValue
): NormalizedRecallCard[] {
  const list = [...(cards ?? [])];
  switch (sortOption) {
    case 'date_desc':
      return list.sort((a, b) => {
        const dateA = a.recallDate ? new Date(a.recallDate).getTime() : 0;
        const dateB = b.recallDate ? new Date(b.recallDate).getTime() : 0;
        return dateB - dateA;
      });
    case 'date_asc':
      return list.sort((a, b) => {
        const dateA = a.recallDate ? new Date(a.recallDate).getTime() : 0;
        const dateB = b.recallDate ? new Date(b.recallDate).getTime() : 0;
        return dateA - dateB;
      });
    case 'severity_desc':
      return list.sort((a, b) => {
        const sa = (a.severity ?? '').toLowerCase();
        const sb = (b.severity ?? '').toLowerCase();
        if (!sa && !sb) {
          return 0;
        }
        if (!sa) {
          return 1;
        }
        if (!sb) {
          return -1;
        }
        return severityRank(sa) - severityRank(sb);
      });
    default:
      return list;
  }
}

export function getRecallCardTitle(card: NormalizedRecallCard): string {
  switch (card.recallType) {
    case 'drugs':
      return card.title ?? card.company ?? '';
    case 'food':
      return card.company ?? '';
    case 'medical devices':
      return card.company ?? '';
    case 'vehicle':
      return card.vehicle ?? '';
    case 'consumer product':
      return card.productName ?? card.description ?? '';
    default:
      return '';
  }
}

export function buildResultsPayload(
  params: RecallsSearchParams,
  data: HookData
): ResultsPayload | null {
  const { pill, displayTerm } = params;

  if (params.vehicleParams) {
    const items = vehicleRecallsToRecallItems(data.vehicleData?.recalls ?? []);
    const sorted = sortRecallItemsByDate(items);
    return {
      title: displayTerm,
      count: sorted.length,
      recallType: 'Vehicle',
      cards: normalizeRecallsForCards(sorted),
    };
  }

  if (pill === 'Food') {
    const fdaItems = fdaRecordsToRecallItems(data.fdaData, 'fdaFood');
    const usdaItems = usdaRecordsToRecallItems(data.usdaData);
    const combined = sortRecallItemsByDate([...fdaItems, ...usdaItems]);
    return {
      title: displayTerm,
      count: combined.length,
      recallType: 'Food',
      cards: normalizeRecallsForCards(combined),
    };
  }

  if (params.fdaParams) {
    const source =
      pill === 'Drugs' ? ('fdaDrug' as const) : ('fdaDevice' as const);
    const recallType = pill === 'Drugs' ? 'Drug' : 'Medical Device';
    const items = fdaRecordsToRecallItems(data.fdaData, source);
    const sorted = sortRecallItemsByDate(items);
    return {
      title: displayTerm,
      count: sorted.length,
      recallType,
      cards: normalizeRecallsForCards(sorted),
    };
  }

  if (params.cpscParams) {
    const items = cpscRecordsToRecallItems(data.cpscData);
    const sorted = sortRecallItemsByDate(items);
    return {
      title: displayTerm,
      count: sorted.length,
      recallType: 'Consumer Product',
      cards: normalizeRecallsForCards(sorted),
    };
  }

  return null;
}

export function buildRecallReportUrl(
  card: NormalizedRecallCard,
  vehicleParams: VehicleRecallsParams | null | undefined
): string {
  const params = new URLSearchParams();
  params.set('source', card.source);

  if (card.source === 'vehicle') {
    const vehicleRecallId = card.recallNumber ?? card.title;
    if (vehicleRecallId) {
      params.set('oem_recall_number', vehicleRecallId);
    }
    if (vehicleParams) {
      for (const [key, value] of Object.entries(vehicleParams)) {
        if (value) {
          params.set(key, value);
        }
      }
    }
  } else if (card.recallNumber) {
    params.set('recall_number', card.recallNumber);
  }

  return `/dashboard/court-cases/recalls/report?${params.toString()}`;
}
